import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { checkServerVersion } from '../app/versionCheck.ts';
import { useAppStore } from '../state/appStore.ts';
import { useSettings } from '../state/settingsStore.ts';
import { isReducedMotion } from '../state/applySettings.ts';
import { waitForFonts } from '../styles/fonts.ts';
import { detectPreset, readDeviceInfo } from '../systems/graphics/detect.ts';
import { Button, Logo, ProgressBar } from '../ui/index.ts';
import styles from './Splash.module.css';

type Step = 'fonts' | 'graphics' | 'language' | 'ready';

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Cold-start screen: animated neon logo, themed loading, the real start-up work (fonts,
 * device detection, language, protocol version) and a "tap to start" gate that also gives
 * the browser the user gesture that audio will need.
 */
export function Splash() {
  const { t } = useTranslation('splash');
  const { t: tc } = useTranslation('common');
  const settings = useSettings();
  const markBooted = useAppStore((s) => s.markBooted);
  const setDetection = useAppStore((s) => s.setDetection);
  const [progress, setProgress] = useState(0);
  const [step, setStep] = useState<Step>('fonts');
  const [updateRequired, setUpdateRequired] = useState(false);
  const startRef = useRef<HTMLButtonElement>(null);
  const reduced = isReducedMotion(settings);

  useEffect(() => {
    let cancelled = false;
    const began = performance.now();
    const run = async () => {
      await waitForFonts();
      if (cancelled) return;
      setStep('graphics');
      setProgress(35);

      const info = readDeviceInfo();
      setDetection(detectPreset(info), info.gpuRenderer !== null);
      await sleep(reduced ? 0 : 250);
      if (cancelled) return;
      setStep('language');
      setProgress(65);

      const version = await checkServerVersion();
      if (cancelled) return;
      setProgress(90);
      if (version.status === 'update-required') {
        setUpdateRequired(true);
        return;
      }

      // Let the logo finish drawing before offering the start button.
      const minimum = reduced ? 0 : 2300;
      await sleep(Math.max(0, minimum - (performance.now() - began)));
      if (cancelled) return;
      setProgress(100);
      setStep('ready');
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [reduced, setDetection]);

  useEffect(() => {
    if (step === 'ready') startRef.current?.focus();
  }, [step]);

  return (
    <main className={styles['splash']} aria-busy={step !== 'ready'}>
      <div className={styles['center']}>
        <Logo animate size="lg" label={tc('appName')} />
        <p className={styles['tagline']}>{t('tagline')}</p>
      </div>
      <div className={styles['footer']}>
        {updateRequired ? (
          <div role="alert" className={styles['update']}>
            <strong>{t('updateRequired')}</strong>
            <span>{t('updateBody')}</span>
          </div>
        ) : step === 'ready' ? (
          <Button ref={startRef} size="lg" onClick={markBooted}>
            {t('tapToStart')}
          </Button>
        ) : (
          <div className={styles['loading']}>
            <ProgressBar value={progress} label={tc('loading')} tone="blueprint" />
            <p className={styles['step']} aria-live="polite">
              {t(`steps.${step}`)}
            </p>
          </div>
        )}
        <p className={styles['version']}>{tc('version', { version: __APP_VERSION__ })}</p>
      </div>
    </main>
  );
}
